import clsx from 'clsx';
import { ChangeEvent, KeyboardEvent, MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { KeyCode } from '@vestico/utils';

import style from './EntryFieldInput.module.css';

export interface EntryValueConfig {
  digitsCount: number;
  maxValue: number;
  suffix?: string;
}

export interface EntryFieldInputProps {
  fieldID: string;
  currentValue: string;
  valueConfig: EntryValueConfig;
  // eslint-disable-next-line react/require-default-props
  showError?: boolean;
  onInput: (newValue: string) => string;
}

export const EntryFieldInput = ({ fieldID, currentValue, valueConfig, showError, onInput }: EntryFieldInputProps) => {
  const { digitsCount, suffix } = valueConfig;

  const [textFieldIsEmpty, setTextFieldIsEmpty] = useState(true);
  const [animateInputError, setAnimateInputError] = useState(false);

  const updateConvertedValue = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      e.preventDefault();

      const newIndexValueString = e.currentTarget.value;
      const newValue = onInput(newIndexValueString);
      const didChangeValue = newIndexValueString !== newValue;
      const didRejectValue = currentValue === newValue && newValue !== '0';
      setAnimateInputError(didRejectValue);
      setTextFieldIsEmpty(newIndexValueString.length === 0 || (didChangeValue && (newValue === '0' || newValue.length === 0)));
      e.currentTarget.value = newValue;
    },
    [currentValue, onInput]
  );

  const onInputKeyUp = useCallback((e: KeyboardEvent<HTMLInputElement>) => {
    if (e.code === KeyCode.enter) {
      e.currentTarget.blur();
    }
    e.preventDefault();
  }, []);

  const selectText = useCallback((e: MouseEvent<HTMLInputElement>) => {
    const target = e.currentTarget;
    if (target) {
      target.setSelectionRange(0, target.value.length);
    }
  }, []);

  useEffect(() => {
    if (!animateInputError) {
      return;
    }

    setTimeout(() => {
      setAnimateInputError(false);
    }, 500);
  }, [animateInputError]);

  const inputRef = useRef<HTMLInputElement>(null);

  const showEmptyText = textFieldIsEmpty && (!currentValue || currentValue.length === 0 || currentValue === '0');
  const inputID = useMemo(() => (suffix ? `${fieldID}-${suffix}` : fieldID), [fieldID, suffix]);

  const inputFragment = (
    <input
      className={clsx(
        style.input,
        { [style.animate_input_error]: animateInputError },
        { [style.input_digits_1]: digitsCount === 1 },
        { [style.input_digits_2]: digitsCount === 2 },
        { [style.input_digits_3]: digitsCount === 3 },
        { [style.input_digits_4]: digitsCount === 4 },
        { [style.input_error]: showError }
      )}
      onClick={selectText}
      type="text"
      inputMode="numeric"
      onChange={updateConvertedValue}
      value={showEmptyText ? '' : currentValue}
      onKeyUp={onInputKeyUp}
      ref={inputRef}
      id={inputID}
      aria-labelledby={suffix ? undefined : inputID}
    />
  );

  const suffixFragment = suffix ? (
    <label className={clsx(style.suffixLabel)} htmlFor={inputID}>
      {suffix}
    </label>
  ) : null;

  return (
    <div className={clsx(style.root)}>
      {inputFragment}
      {suffixFragment}
    </div>
  );
};
